<template>
  <v-dialog v-model="dialog" persistent width="1024">
    <v-card>
      <v-card-title>
        Detalii cerere
        <v-spacer></v-spacer>
        <v-btn icon dark color="black" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-form>
          <v-container>
            <h3 class="custom-heading">Datele despre cerere</h3>
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field v-model="user" label="Solicitant" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="facultatea" label="Facultatea" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="frecventa" label="Frecvența" readonly></v-text-field>
              </v-col>
              
              <v-col cols="12" sm="4">
                <v-text-field v-model="ciclu_de_studii" label="Ciclul de studii" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="specialitatea" label="Specialitatea" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="grupa" label="Grupa" readonly></v-text-field>
              </v-col>
              
              <v-col cols="12" sm="6">
                <v-text-field v-model="semestru" label="Semestrul" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="disciplina" label="Disciplina" readonly></v-text-field>
              </v-col>
              
              <v-col cols="12" sm="6">
                <v-text-field v-model="cause" label="Tipul cererii" readonly></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="date" label="Data emiterii" readonly></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-textarea v-model="explicatia" label="Explicația" readonly auto-grow></v-textarea>
              </v-col>
              
              <v-col cols="12">
                <h3 class="custom-heading">Modificări de note</h3>
                <v-data-table
                  :headers="gradeHeaders"
                  :items="gradeChanges"
                  hide-default-footer
                  class="elevation-1"
                >
                  <template v-slot:item.old_value="{ item }">
                    <span :class="{'red--text': item.new_value !== item.old_value}">{{ item.old_value }}</span>
                  </template>
                  
                  <template v-slot:item.new_value="{ item }">
                    <span :class="{'green--text': item.new_value !== item.old_value}">{{ item.new_value }}</span>
                  </template>
                </v-data-table>
              </v-col>
              
              <v-col cols="12">
                <h3 class="custom-heading">Istoricul comentariilor</h3>
                <v-card outlined class="mt-2 mb-4">
                  <v-card-text>
                    <div v-if="!commentsHistory || commentsHistory.length === 0" class="text-subtitle-1">
                      Nu există comentarii anterioare
                    </div>
                    <template v-else>
                      <div v-for="(item, index) in commentsHistory" :key="index" class="mb-3">
                        <div class="d-flex align-center">
                          <strong>{{ item.user }}</strong>
                          <span class="ml-2 grey--text text--darken-1">({{ formatDate(item.date) }})</span>
                          <v-chip x-small class="ml-2" :color="getStatusColor(item.status)">
                            {{ item.status }}
                          </v-chip>
                        </div>
                        <div class="ml-3 mt-1" v-if="item.comment">{{ item.comment }}</div>
                        <v-divider v-if="index < commentsHistory.length - 1" class="my-2"></v-divider>
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" v-if="canApproveOrReject">
                <v-textarea
                  v-model="comment"
                  label="Adăugați comentariul dvs."
                  persistent-hint
                  hint="Comentariul va fi salvat împreună cu acțiunea dvs."
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
          <v-card-actions v-if="canApproveOrReject">
            <v-row justify="end">
              <v-btn
                style="color: white; background-color: #e53935"
                variant="text"
                @click="showConfirmCard('reject')"
                class="mr-2 mt-2"
              >
                Respingere
              </v-btn>
              <v-btn
                style="color: white; background-color: #2d8654"
                variant="text"
                @click="showConfirmCard('approb')"
                class="mr-2 mt-2"
              >
                Aprobare
              </v-btn>
              <v-btn
                v-if="isOperator"
                style="color: white; background-color: #2196f3"
                variant="text"
                @click="showConfirmCard('complete')"
                class="mr-2 mt-2"
              >
                Marcare ca îndeplinit
              </v-btn>
            </v-row>
          </v-card-actions>
        </v-form>
      </v-card-text>
      <ConfirmCard v-if="confirmCard" :title="confirmTitle" @cancel="cancelConfirmCard" @confirm="confirmAction" />
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import ConfirmCard from "@/components/elements/ConfirmCard.vue";
import { mapGetters } from "vuex";

// Response type IDs (same as TeacherApprovalDetails.vue and the server's teacherFlow.ts)
const ResponseType = {
  TEACHER_CREATED: 13,       // Emis de profesor
  DEAN_APPROVED: 14,         // Aprobat de Decan
  RECTOR_APPROVED: 3,        // Aprobat de Rector
  COMPLETED: 9,              // Îndeplinit
  REJECTED: 4                // Respins
};

export default {
  components: {
    ConfirmCard,
  },
  data() {
    return {
      dialog: false,
      request: null,
      user: '',
      facultatea: '',
      frecventa: '',
      ciclu_de_studii: '',
      specialitatea: '',
      grupa: '',
      semestru: '',
      disciplina: '',
      explicatia: '',
      cause: '',
      date: '',
      gradeChanges: [],
      comment: '',
      commentsHistory: [],
      confirmCard: false,
      confirmTitle: "",
      pendingAction: null,
      gradeHeaders: [
        { text: 'Student', value: 'student_name', width: '25%' },
        { text: 'Disciplina', value: 'disciplina_name', width: '25%' },
        { text: 'Tip evaluare', value: 'grade_type', width: '20%' },
        { text: 'Valoare veche', value: 'old_value', width: '15%' },
        { text: 'Valoare nouă', value: 'new_value', width: '15%' }
      ]
    };
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.getUser.role === "admin";
    },
    isDean() {
      return this.getUser.role === "usersef";
    },
    isRector() {
      return this.getUser.role === "rector";
    },
    isOperator() {
      return this.getUser.role === "operator";
    },
    isTeacher() {
      return this.getUser.role === "profesor";
    },
    canApproveOrReject() {
      if (!this.request) return false;
      
      // Dean can approve if status is "Emis de profesor"
      if (this.isDean && this.request.status_id === ResponseType.TEACHER_CREATED) {
        return true;
      }
      
      // Rector can approve if status is "Aprobat de Decan"
      if (this.isRector && this.request.status_id === ResponseType.DEAN_APPROVED) {
        return true;
      }
      
      // Operator can mark as completed if status is "Aprobat de Rector"
      if (this.isOperator && this.request.status_id === ResponseType.RECTOR_APPROVED) {
        return true;
      }
      
      return false;
    }
  },
  methods: {
    async open(item) {
      if (!item) return;
      
      this.request = item;
      this.dialog = true;
      
      if (!item.requestId) {
        console.error("Error: No requestId provided for request details");
        this.$emit("error", "Nu s-au putut încărca detaliile cererii: ID lipsă");
        return;
      }
      
      try {
        // Load full request details
        const requestDetails = await api.getTeacherRequestById(item.requestId);
        this.populateFormData(requestDetails);
        
        // Load comments history
        this.commentsHistory = await api.getTeacherRequestCommentsHistory(item.requestId);
      } catch (error) {
        console.error("Error loading request details:", error);
        this.$emit("error", "Nu s-au putut încărca detaliile cererii");
      }
    },
    
    populateFormData(data) {
      this.user = data.user || '';
      this.facultatea = data.facultatea || '';
      this.frecventa = data.frecventa || '';
      this.ciclu_de_studii = data.ciclu_de_studii || '';
      this.specialitatea = data.specialitatea || '';
      this.grupa = data.grupa || '';
      this.semestru = this.formatSemester(data.semestru);
      this.disciplina = data.disciplina || '';
      this.cause = data.cause || '';
      this.date = this.formatCreatedAt(data.created_at || data.date);
      this.explicatia = data.explicatia || '';
      this.gradeChanges = data.gradeChanges || [];
    },
    
    formatCreatedAt(dateString) {
      if (!dateString) return '';
      // Handle both date formats that might come from the server
      try {
        return new Date(dateString).toLocaleString('ro-RO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        console.error("Error formatting date:", e);
        return dateString; // Return the original string if parsing fails
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getStatusColor(status) {
      if (!status) return 'grey';
      
      if (status.includes('Emis')) return 'primary';
      if (status.includes('Aprobat de Decan')) return 'info';
      if (status.includes('Aprobat de Rector')) return 'purple';
      if (status.includes('Respins')) return 'error';
      if (status.includes('Îndeplinit')) return 'success';
      
      return 'grey';
    },
    
    showConfirmCard(action) {
      this.confirmCard = true;
      this.pendingAction = action;
      
      if (action === 'approb') {
        this.confirmTitle = "Doriți să aprobați această cerere?";
      } else if (action === 'reject') {
        this.confirmTitle = "Doriți să respingeți această cerere?";
      } else if (action === 'complete') {
        this.confirmTitle = "Doriți să marcați cererea ca îndeplinită?";
      }
    },
    
    cancelConfirmCard() {
      this.confirmCard = false;
      this.pendingAction = null;
    },
    
    confirmAction() {
      this.confirmCard = false;
      
      if (this.pendingAction === 'approb') {
        this.approveRequest();
      } else if (this.pendingAction === 'reject') {
        this.rejectRequest();
      } else if (this.pendingAction === 'complete') {
        this.completeRequest();
      }
      
      this.pendingAction = null;
    },
    
    async approveRequest() {
      let nextStatusId;
      
      // Determine next status based on role
      if (this.isDean) {
        nextStatusId = ResponseType.DEAN_APPROVED;
      } else if (this.isRector) {
        nextStatusId = ResponseType.RECTOR_APPROVED;
      } else {
        return;
      }
      
      try {
        await this.updateRequestStatus(nextStatusId);
        this.dialog = false;
        this.$emit('update-data');
      } catch (error) {
        console.error("Error approving request:", error);
      }
    },
    
    async rejectRequest() {
      try {
        await this.updateRequestStatus(ResponseType.REJECTED);
        this.dialog = false;
        this.$emit('update-data');
      } catch (error) {
        console.error("Error rejecting request:", error);
      }
    },
    
    async completeRequest() {
      try {
        await this.updateRequestStatus(ResponseType.COMPLETED);
        this.dialog = false;
        this.$emit('update-data');
      } catch (error) {
        console.error("Error marking request as completed:", error);
      }
    },
    
    async updateRequestStatus(statusId) {
      if (!this.request || !this.request.requestId) return;
      
      // The server moves the request to the new stage together with the log entry
      await api.saveTeacherResponse({
        request_teacher_id: this.request.requestId,
        response_type_id: statusId,
        comments: this.comment || ''
      });
    },
    
    // Helper method to format semester from number to string
    formatSemester(semestru) {
      if (typeof semestru === 'number') {
        const romanNumerals = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
        if (semestru >= 1 && semestru <= 10) {
          return `Semestrul ${romanNumerals[semestru]}`;
        }
      }
      return semestru || '';
    }
  }
};
</script>

<style scoped>
.custom-heading {
  margin-bottom: 16px;
  color: #424242;
  text-align: left;
}
</style>